import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { Storage } from '@ubud/storage';
import { Config } from '../values/config';
import { User } from '../models/user';
import { forkJoin, from, Observable, Observer, of } from 'rxjs';
import { Signature } from '../values/signature';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { fromPromise } from 'rxjs-compat/observable/fromPromise';

@Injectable()
export class NacoService {
    public readonly VERSION = 'v1';

    private signature: Signature | null = null;
    private user: User | null = null;
    private refreshToken: Signature | null = null;
    private clientSecret: string | null = null;

    public constructor(
        protected config: Config,
        protected router: Router,
        protected storage: Storage,
        protected http: HttpClient,
        protected location: Location,
    ) {
        if (this.config.clientSecret) {
            this.setClientSecret(this.config.clientSecret);
        }
    }

    public setUser(user: User | null): void {
        this.user = user;
    }

    public getUser(): Observable<User | null> {
        if (null !== this.user) {
            return of(this.user);
        }

        const uri = this.getUri('/users/me');

        return from(this.getSignature()).pipe(
            switchMap((signature: Signature | null) => {
                if (!signature) {
                    return this.refreshUser(this.config.scopes);
                }

                const options = {
                    headers: {
                        Authorization: `${signature.type} ${signature.token}`,
                    },
                };

                return this.http.get<any>(uri, options).pipe(
                    map((res: any) => {
                        if (res.data) {
                            return res.data;
                        }

                        throw new Error('There are no body to be transformed');
                    }),
                    map((data: any) => {
                        const authUser: any = plainToClass(User, data);

                        this.setUser(authUser);

                        return authUser;
                    }),
                    catchError((e: HttpErrorResponse) => {
                        if (401 === e.status) {
                            return this.refreshUser(signature.scopes);
                        }

                        return of(null);
                    }),
                );
            }),
        );
    }

    public getProfilePicture(user: string): string {
        return this.config.endpoint + '/users/' + user + '/picture';
    }

    public getSignature(): Promise<Signature | null> {
        if (this.signature) {
            return Promise.resolve(this.signature);
        }

        return this.storage.get('signature');
    }

    public setSignature(signature: Signature | null): void {
        this.signature = signature;

        this.storage.set('signature', signature);
    }

    public setClientSecret(secret: string): void {
        this.clientSecret = secret;
    }

    public setRefreshToken(refreshToken: Signature): void {
        this.refreshToken = refreshToken;
        this.storage.set('refresh_token', refreshToken);
    }

    public refreshUser(scopes?: string): Observable<User | null> {
        this.setUser(null);
        this.setSignature(null);

        return forkJoin(this.getRefreshToken(), this.getClientSecret()).pipe(
            switchMap((results: any) => {
                const refreshToken: Signature = results[0];
                const clientSecret: string = results[1];

                if (refreshToken && clientSecret) {
                    return this.refreshWithRefreshToken(clientSecret, refreshToken.token, scopes);
                }

                return this.refreshWithSilentAuth(scopes);
            }),
        );
    }

    public refreshWithRefreshToken(clientSecret: string, refreshToken: string, scopes?: string): Observable<User | null> {
        const request = this.http.post(this.getUri('/tokens'), {
            grant_type: 'refresh_token',
            scopes: scopes || this.config.scopes,
            client_id: this.config.clientId,
            client_secret: clientSecret,
            refresh_token: refreshToken,
        });

        return request.pipe(
            tap((tokens: any) => {
                if (tokens && tokens.data) {
                    this.setSignature({
                        expiresIn: tokens.data.expires_in || tokens.data.expiresIn,
                        type: tokens.data.token_type || tokens.data.tokenType,
                        token: tokens.data.access_token || tokens.data.accessToken,
                        scopes: decodeURIComponent(tokens.data.scopes || this.config.scopes),
                    });

                    this.setRefreshToken({
                        type: tokens.data.token_type || tokens.data.tokenType,
                        token: tokens.data.refresh_token || tokens.data.refreshToken,
                    });
                }
            }),
            map((tokens: any) => tokens && tokens.data),
            switchMap((hasToken: boolean) => {
                if (hasToken) {
                    return this.getUser();
                }

                return of(null);
            }),
        );
    }

    public refreshWithSilentAuth(scopes?: string): Observable<User | null> {
        return new Observable<User | null>((observer: Observer<User | null>) => {
            const redirectUri = this.getCurrentOrigin('auth/silent');
            const uri = this.login(scopes || this.config.scopes, redirectUri, true, 'token', false);

            const iframe = document.createElement('iframe');
            iframe.setAttribute('src', uri);
            iframe.style.display = 'none';

            iframe.addEventListener(
                'load',
                async () => {
                    try {
                        const queryString = iframe.contentWindow.location.search.substring(1);

                        iframe.parentNode.removeChild(iframe);

                        if (queryString) {
                            const queryParts = queryString.split('&');
                            const params = {};

                            for (const item of queryParts) {
                                const itemParts = item.split('=');

                                params[itemParts[0]] = itemParts[1];
                            }

                            if (params.hasOwnProperty('access_token')) {
                                this.setSignature({
                                    token: params['access_token'],
                                    type: params['token_type'],
                                    expiresIn: params['expires_in'],
                                    scopes: decodeURIComponent(params['scopes'] || this.config.scopes),
                                });

                                const user = await this.getUser().toPromise();

                                if (user) {
                                    this.setUser(user);
                                }

                                observer.next(user);
                            } else {
                                observer.next(null);
                            }

                            return;
                        }

                        observer.next(null);
                    } catch (e) {
                        observer.next(null);
                    }
                },
                false,
            );

            document.body.appendChild(iframe);
        });
    }

    public getAccessTokenFromAuthCode(code: string, redirectUri: string, scopes?: string): Observable<any> {
        return fromPromise(this.getState()).pipe(
            switchMap((state?: string) => {
                const params: Params = {
                    code,
                    grant_type: 'authorization_code',
                    scopes: scopes || this.config.scopes,
                    client_id: this.config.clientId,
                    client_secret: this.config.clientSecret,
                    redirect_uri: redirectUri,
                };

                if (state) {
                    params.state = state;
                }

                return this.http.post(this.getUri('/tokens'), params);
            }),
            map((res: any) => {
                if (res.data) {
                    return res.data;
                }

                throw new Error('There are no body to be transformed');
            }),
        );
    }

    public login(
        scopes: string,
        redirectUri?: string,
        silent: boolean = false,
        responseType: 'token' | 'code' = 'token',
        needState: boolean = true,
    ): string {
        if (!redirectUri) {
            redirectUri = this.router.url;
        }

        const params: any = {
            response_type: responseType,
            scopes: scopes,
            client: this.config.clientId,
            silent: silent,
            continue: encodeURIComponent(redirectUri),
        };

        if (needState) {
            const state = this.generateState();

            this.storage.set('state', state);
            params.state = state;
        }

        const query: string = this.buildQueryString(params);

        return this.config.endpoint + '/auth?' + query;
    }

    public logout(redirectUri?: string): Promise<string> {
        this.signature = null;
        this.user = null;
        this.refreshToken = null;

        return new Promise(resolve => {
            const clear = [this.storage.remove('signature'), this.storage.remove('user'), this.storage.remove('refresh_token')];

            Promise.all(clear).then(() => {
                let endpoint = this.config.endpoint + '/auth/logout';

                if (redirectUri) {
                    endpoint += '?continue=' + encodeURIComponent(redirectUri);
                }

                resolve(endpoint);
            });
        });
    }

    public getState(): Promise<string | null> {
        return this.storage.get('state');
    }

    public buildNestedUri(route: ActivatedRouteSnapshot): string {
        let uri = '';

        if (route.routeConfig && route.routeConfig.path) {
            let routePath = route.routeConfig.path;

            for (const key in route.params) {
                if (key) {
                    const re = new RegExp(':' + key);

                    routePath = routePath.replace(re, route.params[key]);
                }
            }

            uri += '/' + routePath;
        }

        if (route.children) {
            route.children.forEach((item: ActivatedRouteSnapshot) => {
                uri += this.buildNestedUri(item);
            });
        }

        return uri;
    }

    private getRefreshToken(): Promise<Signature | null> {
        if (null !== this.refreshToken) {
            return Promise.resolve(this.refreshToken);
        }

        return this.storage.get('refresh_token');
    }

    private getClientSecret(): Promise<string | null> {
        if (null !== this.clientSecret) {
            return Promise.resolve(this.clientSecret);
        }

        return this.storage.get('client_secret');
    }

    private getUri(path: string): string {
        return this.config.endpoint + '/api/' + this.VERSION + path;
    }

    public getCurrentOrigin(path?: string): string {
        return window.location.origin + this.location.prepareExternalUrl(path || '');
    }

    private generateState(): string {
        const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let text = '';

        for (let i = 0; i < 5; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }

        return text;
    }

    private buildQueryString(params: object): string {
        const queryParams = Object.keys(params).map((key: string) => {
            return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
        });

        return queryParams.join('&');
    }

    public goToAccountEndpoint(route: ActivatedRouteSnapshot, responseType?: 'token' | 'code'): void {
        const ltrim = text => text.replace(/^\/+/, '');

        const next = this.getCurrentOrigin('/' + ltrim(this.buildNestedUri(route.root)));

        this.storage.set('naco_intended_url', next).then(() => {
            this.redirect(
                this.login(this.config.scopes, this.getCurrentOrigin('/auth'), false, responseType || this.config.responseType, true),
            );
        });
    }

    public goToIntendedUrl(): void {
        this.storage.get<string>('naco_intended_url').then(url => {
            this.storage.remove('naco_intended_url');
            this.redirect(url || '/');
        });
    }

    public redirect(url: string): void {
        window.location.href = url;
    }
}
